/**
 * client/types.ts
 * Responsabilidad: Definición de tipos de datos e interfaces para el estado cliente y la interpolación.
 *
 * Mantiene desacoplado el frontend de los schemas de Node del backend, asegurando
 * tipado estricto sin dependencias innecesarias en el bundle del navegador.
 */

import type { PowerUpType, QuestType } from "../src/shared/constants";
import { type PlayerCosmeticsConfig, DEFAULT_COSMETICS } from "./cosmetics";

/**
 * Estado interpolado de un jugador para suavizar la transición visual entre ticks de 30Hz del servidor.
 */
export interface InterpolatedPlayer {
    /** Identificador de sesión único del jugador en la sala Colyseus */
    sessionId: string;

    /** Posición autoritativa X recibida del servidor (última snapshot conocida) */
    targetX: number;

    /** Posición autoritativa Y recibida del servidor (última snapshot conocida) */
    targetY: number;

    /** Posición visual X renderizada en el canvas (aproximada suavemente en cada frame) */
    renderX: number;

    /** Posición visual Y renderizada en el canvas (aproximada suavemente en cada frame) */
    renderY: number;

    /** Masa actual del jugador (determina el radio de renderizado) */
    mass: number;

    /** Elemento asignado al jugador (determina el color del círculo) */
    element: string;

    /** Timestamp en milisegundos (performance.now) de la última actualización autoritativa */
    lastServerUpdate: number;

    /** Indica si el jugador ya recibió su primera posición real o requiere snap inmediato */
    initialized: boolean;

    /** Indica si el jugador está vivo o esperando respawn */
    alive: boolean;

    /** Timestamp en ms (Date.now) en el que reaparecerá el jugador */
    respawnAt: number;

    /** Indica si el jugador es un bot controlado por IA */
    isBot: boolean;

    /** Cantidad acumulada de jugadores absorbidos en la sesión */
    kills: number;

    /** Cantidad acumulada de muertes en la sesión */
    deaths: number;

    /** Alias del jugador para identificación visual */
    alias: string;

    // Power-ups activos
    invulnerableUntil: number;
    speedBoostUntil: number;
    hasShield: boolean;
    doubleMassUntil: number;
    frozenUntil: number;
    freezeCooldownUntil: number;

    // Gameplay y Personalizacion
    skin: string;
    cosmetics?: PlayerCosmeticsConfig;
    lastElementChangeAt: number;

    /** Timestamp en ms (performance.now) de inicio de la vida actual */
    lifeStartTime: number;

    vx: number;
    vy: number;
    facingAngle: number;
    killstreak: number;
    isBoosting: boolean;
    isSpectator: boolean;

    // Sprint 29: Pociones y Consumibles Tácticos
    drinkingUntil: number;
    magnetUntil: number;
    elementLockedUntil: number;
    potionCooldownUntil: number;
    activePotionEffect: string;
    potionSlot0: string;
    potionSlot1: string;
    potionSlot2: string;
    cosmicFoodUntil: number;
    rebirthUntil: number;
    /** ID del título honorífico holográfico equipado (Sprint 35) */
    equippedTitle?: string;

    // Sistema de Poderes y Afinidad de Mascotas
    petBuffActive?: boolean;
    petBuffType?: string;
    petBuffUntil?: number;
    petNextBuffAt?: number;
}

/** Datos requeridos por el renderizador para dibujar un jugador interpolado */
export interface RenderablePlayer {
    sessionId: string;
    renderX: number;
    renderY: number;
    mass: number;
    element: string;
    isLocal: boolean;
    alive: boolean;
    isBot: boolean;
    alias: string;
    invulnerableUntil: number;
    speedBoostUntil: number;
    hasShield: boolean;
    doubleMassUntil: number;
    frozenUntil: number;
    skin: string;
    cosmetics?: PlayerCosmeticsConfig;
    vx: number;
    vy: number;
    facingAngle: number;
    killstreak: number;
    isBoosting: boolean;
    kills: number;

    // Sprint 29: Pociones y Consumibles Tácticos
    drinkingUntil: number;
    magnetUntil: number;
    elementLockedUntil: number;
    potionCooldownUntil: number;
    activePotionEffect: string;
    potionSlot0: string;
    potionSlot1: string;
    potionSlot2: string;
    cosmicFoodUntil: number;
    rebirthUntil: number;
    /** ID del título honorífico holográfico equipado (Sprint 35) */
    equippedTitle?: string;

    // Sistema de Poderes y Afinidad de Mascotas
    petBuffActive?: boolean;
    petBuffType?: string;
    petBuffUntil?: number;
    petNextBuffAt?: number;
}

/** Power-up en el mapa, listo para renderizar */
export interface PowerUpRenderable {
    x: number;
    y: number;
    type: PowerUpType;
}

/** Efecto visual efimero al recoger un power-up */
export interface PowerUpPickupEffect {
    id: string;
    x: number;
    y: number;
    type: PowerUpType;
    startTime: number;
    duration: number;
}

/**
 * Factor lambda de suavizado para la interpolación exponencial independiente del framerate:
 * renderPos += (targetPos - renderPos) * (1 - exp(-LAMBDA * dt))
 *
 * Un valor de 12 produce una constante de tiempo de respuesta de aprox. 80ms (1 / 12 s),
 * eliminando el escalonamiento de los 30Hz del servidor mientras mantiene alta reactividad al input.
 */
export const INTERPOLATION_LAMBDA = 12;

/**
 * Crea una nueva entidad de jugador con su estado de interpolación inicializado.
 */
export function createInterpolatedPlayer(
    sessionId: string,
    initialX: number,
    initialY: number,
    mass: number,
    element: string,
    alias: string
): InterpolatedPlayer {
    return {
        sessionId,
        targetX: initialX,
        targetY: initialY,
        renderX: initialX,
        renderY: initialY,
        mass,
        element,
        lastServerUpdate: performance.now(),
        initialized: false,
        alive: true,
        respawnAt: 0,
        isBot: false,
        kills: 0,
        deaths: 0,
        alias,
        invulnerableUntil: 0,
        speedBoostUntil: 0,
        hasShield: false,
        doubleMassUntil: 0,
        frozenUntil: 0,
        freezeCooldownUntil: 0,
        skin: "default",
        cosmetics: { ...DEFAULT_COSMETICS },
        lastElementChangeAt: 0,
        lifeStartTime: performance.now(),
        vx: 0,
        vy: 0,
        facingAngle: 0,
        killstreak: 0,
        isBoosting: false,
        isSpectator: false,
        drinkingUntil: 0,
        magnetUntil: 0,
        elementLockedUntil: 0,
        potionCooldownUntil: 0,
        activePotionEffect: "",
        potionSlot0: "",
        potionSlot1: "",
        potionSlot2: "",
        cosmicFoodUntil: 0,
        rebirthUntil: 0,
    };
}

/**
 * Estado efímero de una animación de muerte local (efecto shrink).
 */
export interface DeathAnimation {
    sessionId: string;
    x: number;
    y: number;
    startRadius: number;
    element: string;
    startTime: number;
}

/**
 * Payload recibido del servidor por el mensaje "elemental-hit".
 */
export interface ElementalHitPayload {
    attackerId: string;
    defenderId: string;
    effectType: string;
    damage: number;
    x: number;
    y: number;
}

/**
 * Efecto visual efímero en el cliente (anillo + partículas + texto).
 */
export interface ElementalHitEffect {
    id: string;
    x: number;
    y: number;
    effectType: string;
    damage: number;
    startTime: number;
    duration: number;
}

/** Punto de la estela de movimiento de un jugador */
export interface TrailPoint {
    x: number;
    y: number;
    radius: number;
    element: string;
    time: number;
    seed: number;
}

/** Partícula efímera al comer comida */
export interface FoodParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    startTime: number;
    duration: number;
    targetSessionId?: string;
}

/** Anillo expansivo cuando un jugador respawnea */
export interface RespawnRing {
    x: number;
    y: number;
    color: string;
    startTime: number;
    duration: number;
}

/** Identificador de skin de jugador */
export type SkinId = "default" | "ring" | "dotted" | "metallic" | "eyes" | "smile";

/**
 * Perfil local del jugador persistido en localStorage.
 * Versionado para permitir migraciones futuras.
 */
export interface PlayerProfile {
    version: number;
    gamesPlayed: number;
    totalKills: number;
    totalDeaths: number;
    bestMass: number;
    /** 0 = sin clasificar; 1..5 = mejor puesto alcanzado en el top 5 */
    bestRank: number;
    totalTimeMs: number;
    currentStreak: number;
    bestStreak: number;
    country?: string;
    /** Milisegundos acumulados por elemento */
    elementUsage: Record<string, number>;
    lastUpdated: number;
}

/** Identificador de paso del tutorial interactivo */
export type TutorialStepId = "identity" | "movement" | "growth" | "danger" | "completed";

/** Estado persistente del tutorial */
export interface TutorialStepState {
    currentStep: TutorialStepId;
    foodEatenCount: number;
    completed: boolean;
}

/** Evento activo o avisado en el cliente */
export interface ClientEvent {
    type: string;
    name: string;
    emoji: string;
    color: string;
    isPositive: boolean;
    x: number;
    y: number;
    radius: number;
    startAt: number;
    endAt: number;
}

export interface MeteorRender {
    x: number;
    y: number;
    impactAt: number;   // timestamp de impacto (server)
    expiresAt: number;  // timestamp de desaparicion (server)
    impacted: boolean;
}

export interface MeteorImpactEffect {
    x: number;
    y: number;
    startTime: number;
    duration: number;
}

export interface GravityCaptureEffect {
    x: number;
    y: number;
    startTime: number;
    duration: number;
}

export type ProximityReactionKind = "steam" | "root-bloom" | "ember-spark";

export interface ProximityParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    kind: ProximityReactionKind;
    birthTime: number;
    duration: number;
}

export interface FloatingCombatText {
    id: string;
    x: number;
    y: number;
    text: string;
    subtext: string;
    color: string;
    startTime: number;
    duration: number;
}

/** Modelo de datos para misiones diarias y logros en el cliente */
export interface QuestItem {
    id: string;
    type: QuestType;
    titleKey: string;
    title: string;
    description: string;
    target: number;
    progress: number;
    claimed: boolean;
    rewardCrystals: number;
    rewardAstralOrbs: number;
    unit?: string;
    isAchievement: boolean;
}

/** Estado integral de misiones, economía dual y temporizadores */
export interface QuestState {
    crystals: number;
    astralOrbs: number;
    dailyQuests: QuestItem[];
    achievements: QuestItem[];
    msUntilReset: number;
    msUntilWeeklyReset?: number;
    adWatchesRemaining: number;
    loginStreak: number;
}

